import { AddressForm } from '../../components/address-form';
import {
  Button,
  ButtonVariants,
  Typography,
} from '../../components/primitives';
import Alert from '../../components/ui/Alert';
import { RequiredFieldIndicator } from '../../components/ui/RequiredFieldIndicator';
import {
  useAddressAlertProps,
  useAddressForm,
} from '../../hooks/address-form.hook';
import { testId } from '../../utils';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

export const AddAddress: React.FC = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.user' });
  const navigate = useNavigate();
  const {
    form,
    regionQuery,
    countries,
    countriesLoading,
    onCancel,
    AddressSchema,
  } = useAddressForm();

  type AddressSchema = z.infer<typeof AddressSchema>;

  const addAddressMutation = Cornerstone.useAddAddress({
    mutation: {
      onSuccess: () => {
        navigate('..', {
          relative: 'route',
        });
      },
      onError: (error: any) => {
        console.error({
          title: 'Failed to add address',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const { regions, loading: regionsLoading, label: regionLabel } = regionQuery;
  const { handleSubmit } = form;

  const onSubmit = React.useCallback(
    (data: AddressSchema) => {
      addAddressMutation.mutate({ data });
    },
    [addAddressMutation],
  );

  const alertProps = useAddressAlertProps(addAddressMutation);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between items-baseline">
        <Typography variant="title-small" className="text-gray-soft">
          {t('addresses.add_address')}
        </Typography>
        <RequiredFieldIndicator />
      </div>
      {alertProps && <Alert {...alertProps} />}
      <AddressForm
        loading={
          addAddressMutation.isLoading || countriesLoading || regionsLoading
        }
        countries={countries}
        regions={regions}
        regionLabel={regionLabel}
        form={form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex gap-2">
          <Button
            {...testId('save')}
            size="sm"
            type="submit"
            onClick={handleSubmit(onSubmit)}
            loading={addAddressMutation.isLoading}
            disabled={addAddressMutation.isLoading}
          >
            {t('settings.account.save_button')}
          </Button>
          <Button
            {...testId('cancel')}
            variant={ButtonVariants.Secondary}
            size="sm"
            onClick={onCancel}
            disabled={addAddressMutation.isLoading}
          >
            {t('settings.account.cancel_button')}
          </Button>
        </div>
      </AddressForm>
    </div>
  );
};
